import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  ScheduleOutlined as ScheduleIcon,
} from '@mui/icons-material';
import { formatDistanceToNowStrict } from 'date-fns';
import { TERMINAL_IMPORT_JOB_STATES } from '@domain/connection/models/ImportJob';
import { estimateRemaining } from '@shared/utils/progressEta';
import { APP_CONFIG } from '../../../../config/app.config';
import { TrackedImportJob } from '../../hooks/useImportJobs';
import {
  isTerminalImportJob,
  useConnectionImportJobs,
} from '../../hooks/useConnectionImportJobs';

const colors = APP_CONFIG.ui.theme.colors;

const isWaiting = (job: TrackedImportJob) =>
  job.status?.state === 'waiting' || job.status?.state === 'waiting-children';

const isRunning = (job: TrackedImportJob) =>
  (!job.status || !TERMINAL_IMPORT_JOB_STATES.includes(job.status.state)) && !isWaiting(job);

const isCompleted = (job: TrackedImportJob) => job.status?.state === 'completed';

const statusMeta = (job: TrackedImportJob): { label: string; color: string } => {
  if (isWaiting(job)) return { label: 'Scheduled', color: colors.secondaryText };
  if (isRunning(job)) return { label: 'Syncing…', color: colors.primary };
  if (isCompleted(job)) return { label: 'Completed', color: colors.success };
  if (job.status?.state === 'not_found') return { label: 'Not found', color: colors.error };
  return { label: 'Failed', color: colors.error };
};

const JobStatusIcon: React.FC<{ job: TrackedImportJob }> = ({ job }) => {
  if (isWaiting(job)) {
    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${colors.secondaryText}1A`,
          flexShrink: 0,
        }}
      >
        <ScheduleIcon sx={{ fontSize: 18, color: colors.secondaryText }} />
      </Box>
    );
  }

  if (isRunning(job)) {
    const progress = job.status?.progress ?? 0;
    const hasProgress = progress > 0;

    return (
      <Box sx={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}>
        <CircularProgress
          variant={hasProgress ? 'determinate' : 'indeterminate'}
          value={hasProgress ? progress : undefined}
          size={40}
          thickness={4}
          color="primary"
        />
        {hasProgress && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color={colors.primary}>
              {progress}%
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isCompleted(job) ? `${colors.success}1A` : `${colors.error}1A`,
        flexShrink: 0,
      }}
    >
      {isCompleted(job) ? (
        <CheckCircleIcon sx={{ fontSize: 18, color: colors.success }} />
      ) : (
        <ErrorOutlineIcon sx={{ fontSize: 18, color: colors.error }} />
      )}
    </Box>
  );
};

interface ConnectionActiveImportsProps {
  /** When set, only jobs for this connection are shown. Omit to show all. */
  connectionId?: string;
  onImportsFinished?: () => void;
}

/**
 * Lists import jobs for a connection (or all connections).
 * Finished jobs stay visible with a checkmark.
 */
const ConnectionActiveImports: React.FC<ConnectionActiveImportsProps> = ({
  connectionId,
  onImportsFinished,
}) => {
  const { displayJobs, activeJobs, isLoading, isFetched } =
    useConnectionImportJobs(connectionId);
  const [hasSeenActiveImport, setHasSeenActiveImport] = useState(false);
  const hasAdvanced = useRef(false);
  const onImportsFinishedRef = useRef(onImportsFinished);
  onImportsFinishedRef.current = onImportsFinished;

  useEffect(() => {
    const hasInProgress =
      activeJobs.length > 0 || displayJobs.some((job) => !isTerminalImportJob(job));
    if (hasInProgress) {
      setHasSeenActiveImport(true);
    }
  }, [activeJobs, displayJobs]);

  const importsFinished =
    !!onImportsFinished &&
    hasSeenActiveImport &&
    isFetched &&
    displayJobs.length > 0 &&
    displayJobs.every(isTerminalImportJob) &&
    activeJobs.length === 0;

  useEffect(() => {
    if (!importsFinished || hasAdvanced.current) return;
    hasAdvanced.current = true;
    onImportsFinishedRef.current?.();
  }, [importsFinished]);

  return (
    <Stack >


      {isLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : displayJobs.length === 0 && (
        <Box display="flex" justifyContent="center" py={2}>
          <Typography variant="body2" color="text.secondary">
            No running imports for this connection.
          </Typography>
        </Box>
      )}


      {displayJobs.map((job) => {
        const { label: statusText, color } = statusMeta(job);
        const eta = isRunning(job)
          ? estimateRemaining(job.startedAt, job.status?.progress ?? 0)
          : null;

        return (
          <Stack
            key={job.id}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ px: 2, py: 1.5 }}
          >
            <JobStatusIcon job={job} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color={colors.primaryText}
                noWrap
              >
                {job.label}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: colors.secondaryText }}
                  noWrap
                >
                  {statusText} ·{' '}
                  {formatDistanceToNowStrict(job.startedAt, { addSuffix: true })}
                  {eta ? ` · ${eta}` : ''}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        );
      })
      }
    </Stack>
  );
};

export default ConnectionActiveImports;
